import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Button, Input, Switch, Checkbox } from 'react-toolbox';
import { cloneDeep, clone, isEqual } from 'lodash';
import cx from 'classnames';
import { urls } from 'helpers/url';
import Card from 'components/Card';
import RadioButtons from 'components/RadioButtons';
import SimpleInput from 'components/SimpleInput';
import EmailLink from 'components/EmailLink';
import StyledSelect from 'components/StyledSelect';
import WarehouseMerchandiseItemTable from '../warehouseMaterials/components/warehouseMerchandiseItemTable';
import PurchaseOrderTable from '../phaseAccounting/components/purchaseOrderTable';
import AssessmentSelection from 'assessmentSelection';
import PhaseLineItemTables from 'phaseLineItemTables';
import MerchandiseItemTable from 'phaseMerchandiseItemTable';
import RelatedPhases from '../relatedPhases';
import PhaseDocuments from './../phaseDocuments';
import { navigate, fetchPhaseDocuments } from 'actionCreators';
import {
  updatePhase,
  updatePhaseTask,
  addEventToPhase,
} from 'actionCreators/phase';
import { requestPhaseItems } from 'actionCreators/phase_items';
import { getPhaseErrors, folderDocuments, getRole } from '../../../../../../../reducers';
import { error, vendorDisplayName } from '../../../../../../../utils';
import { cutQuantityString } from '../../../../../../../utils/lineItemUtils';
import { can } from '../../../../../../../services';
import PhaseType from 'types/phase';
import phaseLineItemShape from 'types/lineItemPropType';
import warehouseMerchandiseItem from 'types/warehouseMerchandiseItem';
import { REQUIRED_CV_FIELDS,
         REQUIRED_CV_FIELDS_IFI,
         ANCHORED_REMOVAL_PLIS,
         ANCHORED_REMOVAL_SLUGS } from 'config/constants';
import styles from 'styles/form';
import S from './styles.scss';

const largeInput = cx(styles.inputs, styles.largeInput);
const smallInput = cx(styles.inputs, styles.smallInput);

class ContractVerificationChecklist extends React.Component {
  constructor(props) {
    super(props);
    const { phase = {}, anchoredRemoval } = this.props;
    const phaseClone = cloneDeep(phase);
    if (anchoredRemoval && Object.keys(phaseClone).length > 0) {
      ANCHORED_REMOVAL_SLUGS.forEach((s) => {
        phaseClone.phase_specific[s] = false;
      });
    }
    this.state = {
      phase: phaseClone,
      newPhaseInfo: false,
      newConstruction: '',
      pavedDriveway: '',
      difficultAccess: '',
      restrictedParking: '',
      elevatorAvailable: '',
      floorBaseHeating: '',
      freshPaintWalls: '',
      petsPresent: '',
      cuttingArea: '',
      rapidSetMortar: '',
      woodSubfloor: '',
      hardyBackerMortar: '',
      rolledGoodsTransitions: '',
      pickup: '',
      gasAppliances: '',
      buildingType: '',
      installerNote: '',
      customerNote: '',
      readyToSubmit: false,
      expressInstallation: '',
      confirmedAnchorRemovals: [],
      ifiDoneStatus: '',
    };
  }

  componentWillMount = () => this.requestRequiredLists();

  componentWillUpdate = (nextProps) => {
    const { documents: { data: nextDocs },
            lineItemsWarehouseMerchandise: nextMerch,
            lineItemsWarehousePads: nextPads } = nextProps;
    const { documents: { data: currDocs },
            lineItemsWarehouseMerchandise: currMerch,
            lineItemsWarehousePads: currPads } = this.props;
    if ((nextDocs.length > 0 && nextDocs.length !== currDocs.length) ||
        (nextMerch.length > 0 && nextMerch.length !== currMerch.length) ||
        (nextPads.length > 0 && nextPads.length !== currPads.length)) {
      this.requestRequiredLists();
    }
  }

  requestRequiredLists = () => {
    this.props.fetchPhaseDocuments(this.props.phase.id);
    this.props.requestPhaseItems(this.props.phase.id, 'phaseLineItems');
    this.props.requestPhaseItems(this.props.phase.id, 'warehouseMerchandiseItems');
    this.setState({ pads: this.parsePads(this.props.lineItemsWarehousePads) });
  }

  nothingToSubmit = () => !this.state.newPhaseInfo;

  handlePhaseBasic = (key, val) => {
    const { phase } = this.state;
    phase[key] = val;
    this.setState({ phase, newPhaseInfo: true });
  }

  handlePhaseSpecific = (key, val) => {
    const { phase, confirmedAnchorRemovals } = this.state;
    const changedAnchoredRemoval = ANCHORED_REMOVAL_SLUGS.includes(key);
    const editedPhase = cloneDeep(phase);
    let editedRemovals = clone(confirmedAnchorRemovals);
    editedPhase.phase_specific[key] = val;
    if (val && key === 'need_move_furniture_oversized') editedPhase.phase_specific.need_move_furniture = true;
    else if (changedAnchoredRemoval) {
      if (val && !editedRemovals.includes(key)) editedRemovals.push(key);
      else if (!val && editedRemovals.includes(key)) {
        editedRemovals = editedRemovals.filter(item => item !== key);
      }
    }
    const updatedState = { phase: editedPhase, newPhaseInfo: true };
    if (!isEqual(editedRemovals, confirmedAnchorRemovals)) {
      updatedState.confirmedAnchorRemovals = editedRemovals;
    }
    this.setState(updatedState);
  }

  handleMillworkSpecific = (key, val) => {
    const { phase } = this.state;
    const editedPhase = cloneDeep(phase);
    editedPhase.phase_specific.millwork_specifics = {
      ...editedPhase.phase_specific.millwork_specifics,
      [key]: val,
    };
    this.setState({ phase: editedPhase, newPhaseInfo: true });
  }

  handleVal = (key, val) => this.setState({ [key]: val });

  handleInstallerNote = e => this.handleVal('installerNote', e.target.value);

  handleCustomerNote = e => this.handleVal('customerNote', e.target.value);

  handleReadyToSubmit = val => this.setState({ readyToSubmit: val === 'Yes' });

  completeCvChecklist = () => {
    const { phase, phase: { id, phase_specific }, expressInstallation } = this.state;
    const note = this.contractorNotes();
    phase.contractor_notes = note;
    if (phase.date_sold || '') delete phase.date_sold;
    const expressInstallBool = expressInstallation === 'Yes' ? 'true' : 'false';
    this.props.updatePhase({ ...phase, phase_specific_attributes: phase_specific }, {})
      .then(() => this.props.updatePhaseTask(
        id, 'cv_checklist', {
          task: { status: 'complete' },
          event: { data: {} },
          phase: { status: 'waiting_for_product' },
          express_installation: expressInstallBool,
        },
      ))
      .then(() => this.props.navigate(urls.phaseHistory(phase)))
      .catch(() => { });
  }

  parsePads = lineItemsPad => lineItemsPad.map(lineItem => ({
    cut_quantity: cutQuantityString(lineItem.warehouse_quantity),
    description: lineItem.description,
  }));

  fieldNeeded = (field) => {
    const { phase: { surface_type, net_square_footage }, woodSubfloor } = this.state;
    switch (field) {
      case 'rapidSetMortar':
        return surface_type.name === 'Tile';
      case 'woodSubfloor':
        return surface_type.name !== 'Carpet' && (!net_square_footage || net_square_footage < 150);
      case 'hardyBackerMortar':
        return surface_type.name === 'Tile' && woodSubfloor === 'Yes';
      default:
        return true;
    }
  }

  requiredFieldsMissing = () => {
    const { phase: { product_type, surface_type: { name } }, anchoredRemoval } = this.props;
    let warning = '';
    // If product type is "STK" create a new object because-
    // another property is needed for validation
    const requiredFieldsObj = product_type === 'STK' ? Object.assign({}, REQUIRED_CV_FIELDS) : REQUIRED_CV_FIELDS;
    if (product_type === 'STK' && name === 'Carpet') requiredFieldsObj.expressInstallation = 'Express Installation';
    const requiredFieldsKeys = Object.keys(requiredFieldsObj);
    requiredFieldsKeys.forEach((f) => {
      if (this.fieldNeeded(f) && !this.state[f]) {
        warning += warning ? `, ${requiredFieldsObj[f]}` : `Please input a(n) ${requiredFieldsObj[f]}`;
      }
    });
    if (anchoredRemoval && this.state.confirmedAnchorRemovals < 1) {
      warning += warning ? ', Hardwood Removal Type' : 'Please select an Hardwood Removal Type';
    }
    return warning;
  }

  requiredFieldsMissingIfi = () => {
    let warning = '';
    Object.keys(REQUIRED_CV_FIELDS_IFI).forEach((f) => {
      if (!this.state[f]) {
        warning += warning
          ? `, ${REQUIRED_CV_FIELDS_IFI[f]}`
          : `Please input a(n) ${REQUIRED_CV_FIELDS_IFI[f]}`;
      }
    });
    return warning;
  }

  note = val => val || 'N/A';

  gasAppliancesNote = () => (this.state.gasAppliances === 'Yes' ? ' - gas appliances on worksite' : '');

  contractorNotes = () => {
    const vendor = vendorDisplayName(this.state.phase.source, this.state.phase.workroom.name);
    return `${this.props.phase.contractor_notes || ''}

-->> INSTALLER NOTES: ${this.note(this.state.installerNote)} <<--

-->> CUSTOMER RESPONSIBILITIES: ${this.note(this.state.customerNote) + this.gasAppliancesNote()} <<--

-->> NEW CONSTRUCTION: ${this.note(this.state.newConstruction)} <<--

-->> PAVED DRIVEWAY: ${this.note(this.state.pavedDriveway)} <<--

-->> DIFFICULT ACCESS: ${this.note(this.state.difficultAccess)} <<--

-->> RESTRICTED PARKING ACCESS: ${this.note(this.state.restrictedParking)} <<--

-->> ELEVATOR AVAILABLE: ${this.note(this.state.elevatorAvailable)} <<--

-->> FLOOR/BASEBOARD HEATING PRESENT: ${this.note(this.state.floorBaseHeating)} <<--

-->> FRESHLY PAINTED WALLS: ${this.note(this.state.freshPaintWalls)} <<--

-->> PETS AT THE SITE: ${this.note(this.state.petsPresent)} <<--

-->> PLACE TO CUT MATERIALS: ${this.note(this.state.cuttingArea)} <<--

-->> BUILDING TYPE: ${this.note(this.state.buildingType)} <<--

-->> SUPPLYING TRANSITIONS FOR ROLLED GOODS: ${this.note(this.state.rolledGoodsTransitions)} <<--

-->> PICKUP REQUIRED: ${this.note(this.state.pickup)} <<--

-->> RAPID SETTING MORTAR: ${this.note(this.state.rapidSetMortar)} <<--

-->> WOOD SUBFLOOR: ${this.note(this.state.woodSubfloor)} <<--

-->> HARDY BACKER + MORTAR: ${this.note(this.state.hardyBackerMortar)} <<--
********
All sub contractors should have the following items:
${vendor === 'Lowe\'s' ? `--Lowe's T shirt
--Lowe's Badge
--Lowe's Installation yard sign` : ''}
--Oil Shield for Drive Way parking
--Tarp for protecting customers property
--A smile
Todos los subcontratistas deben tener los siguientes elementos:
${vendor === 'Lowe\'s' ? `--Camiseta Lowe's
--Insignia de Lowe's
--Lowe's Signo de patio de instalación` : ''}
--Escudo de aceite para estacionamiento de garaje
--Lona para proteger propiedad de los clientes
--Una sonrisa
********`;
  };

  buttons = (isReadyToSubmitRadio = false) => ([
    { label: 'YES', value: 'Yes' }, { label: 'NO', value: 'No' },
  ].concat(isReadyToSubmitRadio ? [] : { label: 'N/A', value: 'N/A' }));

  buildingTypeOptions = () => ([
    { label: 'Residential', value: 'Residential' },
    { label: 'Commercial', value: 'Commercial' },
    { label: 'Mobile Home', value: 'Mobile Home' },
  ]);

  completeCvChecklistIfi = () => {
    const { phase, phase: { id, phase_specific } } = this.state;
    const note = this.contractorNotesIfi();
    phase.contractor_notes = note;
    if (phase.date_sold || '') delete phase.date_sold;
    this.props.updatePhase({ ...phase, phase_specific_attributes: phase_specific }, {})
      .then(() => this.props.updatePhaseTask(
        id, 'cv_checklist', {
          task: { status: 'complete' },
          event: { data: {} },
          phase: { status: this.state.ifiDoneStatus },
        },
      ))
      .then(() => this.props.navigate(urls.phaseHistory(phase)))
      .catch(() => { });
  }

  contractorNotesIfi = () => `${this.props.phase.contractor_notes || ''}

-->> INSTALLER NOTES: ${this.note(this.state.installerNote)} <<--

-->> CUSTOMER RESPONSIBILITIES: ${this.note(this.state.customerNote)} <<--

-->> DIFFICULT ACCESS: ${this.note(this.state.difficultAccess)} <<--

-->> RESTRICTED PARKING ACCESS: ${this.note(this.state.restrictedParking)} <<--

-->> ELEVATOR AVAILABLE: ${this.note(this.state.elevatorAvailable)} <<--

-->> BUILDING TYPE: ${this.note(this.state.buildingType)} <<--

-->> PICKUP REQUIRED: ${this.note(this.state.pickup)} <<--`;

  renderIfi = () => <div>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Getting Started</h4>
      </div>
      <div className={S.checklistContainer}>
        <h5>Linking The Assessment Phase To The Installation Phase</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            Linking the phases will pull all Assessment and Refigure documents
            into the Installation Phase
          </li>
        </ul>
        <br />
        <RelatedPhases cv />
        <br />
        <h5>Selecting/Verifying The Matching Assessment Document</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            Select appropriate assessment from the dropdown below (If the
            correct one is not there, upload it in the Upload box below, under
            {'\'Assessment/Refigure\''})
          </li>
          <li>
            Click the green button to open the doc in a new window to visually
            confirm information on the assessment
          </li>
          <li>
            KEEP THE ASSESSMENT OPEN FOR THE DURATION OF THIS CHECKLIST - it is
            needed as a reference
          </li>
        </ul>
        <br />
        <AssessmentSelection cv parentDocs={this.props.documents.data} />
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Verifying Product/Labor</h4>
      </div>
      <div className={S.checklistContainer}>
        <h5>Verifying Product</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            Cross-check that all materials listed in the Detail Sheet are listed
            on the contract and sold in the correct quantities.
          </li>
          <li>
            Compare the measurements on the Detail Sheet to what was sold on
            the {'\'Extended Order Details\''}.
          </li>
          <li>Confirm that the door/window sizes match exactly.</li>
          <li>
            If measurements do not match, submit a Support Request to resolve
            the issue.
          </li>
        </ul>
        <br />
        <PurchaseOrderTable
          cv phaseId={this.state.phase.id} documents={this.props.documents.data}
        />
        <br />
        <MerchandiseItemTable
          phaseId={this.state.phase.id} phaseSource={this.state.phase.source} cv
        />
        <br />
        <h5>Identifying/Inputting Custom Labor</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            The layout of assessment/refigure docs may vary, so finding custom
            labor may vary from document to document
          </li>
          <li>
            Utilize your computer{'\''}s built-in search function (CTRL+f) to search
            for key words such as: {'\'Custom\''}, {'\'Additional\''}, {'\'Additional Custom\''},
            {'\'Custom Labor\''}, etc. (these are not the only things you can search)
          </li>
          <li>
            If the document has a {'\'Service Items\''} Section, if a item in that
            has {'\'Custom\''} in the {'\'Item Number\''} column, it is a custom labor
          </li>
          <li>
            If the document has a table that starts on the first or second page
            and has a column titled {'\'LN\''}, in that table look for any Labor
            described as custom, and there is also a specific row called
            {'\'Additional Custom Work\''} (it{'\''}s usually {'\'LN 57\''})
          </li>
          <li>Input custom labor in the PO table below (where applicable)</li>
        </ul>
        <br />
        <PhaseLineItemTables cv phaseId={this.state.phase.id} />
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Input Phase Info</h4>
      </div>
      <div className={S.checklistContainer}>
        <Input
          label="Number of Doors"
          className={largeInput}
          value={this.state.phase.phase_specific.millwork_specifics
            ? this.state.phase.phase_specific.millwork_specifics.num_doors || ''
            : ''}
          onChange={val => this.handleMillworkSpecific('num_doors', val)}
        />
        <Input
          label="Number of Windows"
          className={largeInput}
          value={this.state.phase.phase_specific.millwork_specifics
            ? this.state.phase.phase_specific.millwork_specifics.num_windows || ''
            : ''}
          onChange={val => this.handleMillworkSpecific('num_windows', val)}
        />
        <Input
          label="Year Built"
          className={smallInput}
          value={this.state.phase.year_built || ''}
          onChange={val => this.handlePhaseBasic('year_built', val)}
          error={error('year_built', this.props.errors)}
        />
        <Switch
          name="hasPower"
          label="Has Power"
          onChange={val => this.handlePhaseSpecific('customer_has_power', val)}
          checked={this.state.phase.phase_specific.customer_has_power}
        />
        <Switch
          name="hasClimateControl"
          label="Has Climate Control"
          onChange={val => this.handlePhaseSpecific('customer_has_climate_control', val)}
          checked={this.state.phase.phase_specific.customer_has_climate_control}
        />
        <Switch
          name="permitRequired"
          label="Permit Required"
          onChange={val => this.handleMillworkSpecific('permit_required', val)}
          checked={!!(this.state.phase.phase_specific.millwork_specifics &&
            this.state.phase.phase_specific.millwork_specifics.permit_required)}
        />
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Input Contractor Note Info</h4>
      </div>
      <div className={S.checklistContainer}>
        <RadioButtons
          label={'Difficult Access?'}
          onChange={val => this.handleVal('difficultAccess', val)}
          selectedValue={this.state.difficultAccess}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Restricted Parking Access?'}
          onChange={val => this.handleVal('restrictedParking', val)}
          selectedValue={this.state.restrictedParking}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'If Multi-Family Unit, Is Elevator Available?'}
          onChange={val => this.handleVal('elevatorAvailable', val)}
          selectedValue={this.state.elevatorAvailable}
          radioButtons={this.buttons()}
        />
        <br />
        <StyledSelect
          className={largeInput}
          label="Building Type"
          onChange={val => this.handleVal('buildingType', val)}
          options={this.buildingTypeOptions()}
          value={this.state.buildingType || ''}
          placeholder={'Select Building Type'}
        />
        <RadioButtons
          label={'Pickup Required?'}
          onChange={val => this.handleVal('pickup', val)}
          selectedValue={this.state.pickup}
          radioButtons={this.buttons()}
        />
        <SimpleInput
          label="Installer Note"
          text={this.state.installerNote}
          errors={this.props.errors.installerNote}
          onChange={this.handleInstallerNote}
        />
        <SimpleInput
          label="Customer Responsibilities"
          text={this.state.customerNote}
          errors={this.props.errors.customerNote}
          onChange={this.handleCustomerNote}
        />
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Confirm And Submit</h4>
      </div>
      <div className={S.checklistContainer}>
        {this.requiredFieldsMissingIfi() ? (
          <p className={S.checklistError}>{this.requiredFieldsMissingIfi()}</p>
        ) : (<Fragment>
          <RadioButtons
            label={'Where should this phase go?'}
            onChange={val => this.handleVal('ifiDoneStatus', val)}
            selectedValue={this.state.ifiDoneStatus}
            radioButtons={[
              { label: 'Waiting for Product', value: 'waiting_for_product' },
              { label: 'Sent to Scheduling', value: 'scheduling' },
              { label: 'IWOL', value: 'hold' },
            ]}
          />
          <br />
          {this.state.ifiDoneStatus ? (
            <Button
              label="Submit Info And Complete Checklist"
              flat inverse ripple
              className={S.saveButton}
              onClick={() => this.completeCvChecklistIfi()}
            />
          ) : (
            <p className={S.checklistError}>
              You must select a completion status to continue
            </p>
          )}
        </Fragment>)}
      </div>
    </Card>
  </div>;

  renderDefault = () => <div>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Getting Started</h4>
      </div>
      {this.props.phase.product_type === 'STK' &&
       this.props.phase.surface_type.name === 'Carpet' && <div className={S.checklistContainer}>
         <RadioButtons
           label={'Is this an Express Installation?'}
           onChange={val => this.handleVal('expressInstallation', val)}
           selectedValue={this.state.expressInstallation}
           radioButtons={this.buttons(true)}
         />
       </div>}
      <div className={S.checklistContainer}>
        <h5>Linking The Assessment Phase To The Installation Phase</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            Linking the phases will pull all Assessment and Refigure documents
            into the Installation Phase
          </li>
        </ul>
        <br />
        <RelatedPhases cv />
        <br />
        <h5>Selecting/Verifying The Matching Assessment Document</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            Select appropriate assessment from the dropdown below (If the
            correct one is not there, upload it in the Upload box below, under
            {'\'Assessment/Refigure\''})
          </li>
          <li>
            Click the green button to open the doc in a new window to visually
            confirm information on the assessment
          </li>
          <li>
            KEEP THE ASSESSMENT OPEN FOR THE DURATION OF THIS CHECKLIST - it is
            needed as a reference
          </li>
        </ul>
        <br />
        <AssessmentSelection cv parentDocs={this.props.documents.data} />
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Verifying Product/Labor</h4>
      </div>
      <div className={S.checklistContainer}>
        <h5>How To Verify Assessment Drawings/Merchandise</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            Verify the amount of flooring sold (see the Merchandise Table)
            matches the square footage in the assessment (this can be found on
            either page 1 or 2 depending on the surface type)
          </li>
          <li>
            Once you confirm your detail matches what was sold, you will select
            the floor plan that is attached to the matching detail, therefore
            when looking at the floor plan you want make sure you have no seams
            in the doorways, lay direction, number of rooms, pattern is noted or
            not, no T-seams with pattern or berber carpet (Look for T-seams with
            Pattern or Berber carpet ONLY if a SPIG measure - APEX measures will
            be pushed through with T-seams with Pattern or Berber carpet)
          </li>
        </ul>
        <br />
        <h5>How To Identify An Internal Refigure Is Needed</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            An internal refigure is needed when:
            <ul>
              <li>Carpet sold does not match the amount on the detail</li>
              <li>
                Carpet/Vinyl sold has a pattern, but no pattern on the
                assessment
              </li>
              <li>
                Carpet sold is berber or has a pattern and the floor plan is a
                T-seam
              </li>
              <li>Original floor plan has seams in doorways</li>
              <li>
                Store sells what they want to sell without requesting a refigure
                to add/combine or remove rooms
              </li>
              <li>Sold nail-down/glue-down wood from a laminate assessment</li>
              <li>Sold laminate off a vinyl plank assessment</li>
            </ul>
          </li>
          <br />
          <li>
            Other internal refigures would be needed for onsite issues:
            <ul>
              <li>
                A room needs to be removed or added to know how much material
                and labor is needed
              </li>
              <li>
                Plywood needs to be added to the order once the installer
                arrived on site
              </li>
              <li>
                Change an area from one surface type to another (e.g. carpet
                to hard surface or vice versa)
              </li>
            </ul>
          </li>
        </ul>
        <br />
        <h5>How To Submit A Refigure</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            If an Apex measure, send to Apex:
            <ul>
              <li>
                <EmailLink email="recalcs@apexmeasuring.com">recalcs@apexmeasuring.com</EmailLink>
              </li>
              <li>
                Once the refigured assessment is received, upload it to the
                {'\'Assessments/Refigures\''} folder in the {'\'Documents\''} tab
                and continue verification
              </li>
            </ul>
          </li>
          <br />
          <li>
            If a SPIG measure, request an internal refigure to know how much
            surface type material will be needed with the amount/pattern size
            sold:
            <ul>
              <li>
                Open the linked Assessment Phase, and follow the remaining
                refigure instructions
              </li>
              <li>
                Click the red {'\'Action\''} button (bottom-right of the screen,
                has a white {'\'+\''} symbol)
              </li>
              <li>Select {'\'Override Status\''}</li>
              <li>
                In the pop-up window, select {'\'Refigure\''} from the dropdown menu
              </li>
              <li>
                Include the reason for the refigure in the {'\'Notes\''} section
              </li>
              <li>Click the blue {'\'Override Status\''} button to save</li>
              <li>Click the {'\'Action\''} button again</li>
              <li>Select {'\'Add Flag\''}</li>
              <li>Select {'\'Internal Refigure\''} from the options</li>
              <li>Add a note</li>
              <li>Click {'\'Add Flag\''} to save</li>
            </ul>
          </li>
          <br />
          <li>
            If SPIG measure with T-seams, request an Internal Refigure to eliminate the T-seams:
            <ul>
              <li>
                Navigate to the Assessment Phase
              </li>
              <li>
                Click the Action button
              </li>
              <li>
                Select Override Status
              </li>
              <li>
                In the pop-up window, select Refigure from the dropdown menu
              </li>
              <li>
                Include the reason for the refigure in the Notes section
              </li>
              <li>
                Click the blue Override Status button to save
              </li>
              <li>
                Click the Action button
              </li>
              <li>
                Select Add Flag
              </li>
              <li>
                Select Internal Refigure from the options
              </li>
              <li>
                Add a note
              </li>
              <li>
                Click Add Flag
              </li>
            </ul>
          </li>
          <br />
          <li>
            If Apex measure, send a Refigure Request to Apex at recalcs@apexmeasuring.com
            <ul>
              <li>
                Once the refigured Assessment is received, upload it to the
                {'\'Assessments/Refigures\''} file in the {'\'Documents\''} tab
                and continue verification
              </li>
            </ul>
          </li>
        </ul>
        <br />
        {this.state.phase.product_type === 'SOS' && <Fragment>
          <h5>How To Add SOS PO And For Tracking/Inventory Purposes</h5>
          <br />
          <ul className={S.scriptList}>
            <li>
              In the purchase orders table below, click the blue {'\'Edit\''} button
            </li>
            <br />
            <li>
              In the blank row that populates, enter information in the
              following fields:
              <ul>
                <li>
                  PO # - found in VG under the {'\'Current Items Sold\''} section
                  in the customer{'\''}s profile
                </li>
                <li>Type - select {'\'Special Order\''} from the dropdown menu</li>
                <li>
                  Invoice Status - select {'\'Non-Billable\''} from the dropdown menu
                </li>
                <li>Total - enter {'\'0\''}</li>
              </ul>
            </li>
            <br />
            <li>Click the green {'\'Save\''} button</li>
            <li>Click the green {'\'Done\''} button</li>
          </ul>
          <br />
          <PurchaseOrderTable
            cv phaseId={this.state.phase.id} documents={this.props.documents.data}
          />
          <br />
          <h5>How To Add SOS Product To Warehouse Merchandise Table</h5>
          <br />
          <ul className={S.scriptList}>
            <li>
              In the merchandise table below, click the blue {'\'Edit\''} button
            </li>
            <br />
            <li>
              In the blank row that populates, enter information in the
              following fields:
              <ul>
                <li>
                  PO Number - select the non-billable PO that was just created
                </li>
                <li>Item Name</li>
                <li>
                  QTY Received - enter amount/sqft of surface type material
                </li>
                <li>Date Received</li>
                <li>Note (optional)</li>
                <li>Status - select {'\'On Order\''} from the drop-down list</li>
              </ul>
            </li>
            <br />
            <li>Click the green {'\'Save\''} button</li>
            <li>Click the green {'\'Done\''} button</li>
          </ul>
          <br />
          <WarehouseMerchandiseItemTable
            padInfo={this.state.pads}
            lineItems={this.props.lineItemsWarehouseMerchandise}
            editable={can(this.props.role, 'update', 'warehouse')}
            phaseId={this.state.phase.id}
            cv
          />
          <br />
        </Fragment>}
        {this.state.phase.surface_type.name === 'Carpet' && <Fragment>
          <h5>Handling Berber Carpet Seams & Pattern Match Carpets</h5>
          <br />
          <ul className={S.scriptList}>
            <li>
              In the merchandise table below, click on the item number (this will
              take you to the product on Lowe{'\''}s website)
            </li>
            <li>On page 3 (usually) of the detail, find pattern size</li>
            <li>
              You can also see the pattern match at the bottom right of each page
              of the actual assessment and on the cut sheet
            </li>
            <br />
            <li>
              Berber Carpet Info:
              <ul>
                <li>
                  T-seam definition: Installer makes a head seam between two short
                  fill pieces, and then side-seams them to the start piece (should
                  be no wider than 2ft.) - NOT suitable for berber carpet
                </li>
                <li>
                  Head/Cross Seam Definition: Installer joins two pieces that are
                  cut across the width of the carpet - NOT suitable for berber
                  carpet
                </li>
                <li>
                  When verifying the assessment(s) vs. what was sold and it has
                  been determined that the store sold less carpet than required
                  for a pattern match carpet, an internal refigure will need to be
                  performed to see if we can adjust the layout and use what carpet
                  was sold or if the store needs to order more carpet
                </li>
              </ul>
            </li>
            <br />
            <li>
              Why does patterned material add so much waste to the job?
              <ul>
                <li>
                  When installing carpet or sheet vinyl with a pattern, the
                  installer must match the pattern on all seams
                </li>
                <li>
                  Each fill piece needs to have one pattern repeat (both height
                  width) added to it so the installer can shift it into place
                </li>
                <li>
                  No matching or producing an {'\'economy match\''} could
                  void the warranty
                </li>
              </ul>
            </li>
          </ul>
          <br />
        </Fragment>}
        {this.state.phase.source === 'BJ' && <Fragment>
          <h5>How To Add BJ{"'"}s PO For Tracking/Inventory Purposes</h5>
          <br />
          <ul className={S.scriptList}>
            <li>
              In the purchase orders table below, click the blue {'\'Edit\''} button
            </li>
            <br />
            <li>
              In the blank row that populates, enter information in the
              following fields:
              <ul>
                <li>
                  PO # - enter the purchase order number from BJ{"'"}s system
                  or documents
                </li>
                <li>Type - select {'\'Special Order\''} from the dropdown menu</li>
                <li>
                  Invoice Status - select {'\'Non-Billable\''} from the dropdown menu
                </li>
                <li>Total - enter {'\'0\''}</li>
              </ul>
            </li>
            <br />
            <li>Click the green {'\'Save\''} button</li>
            <li>Click the green {'\'Done\''} button</li>
          </ul>
          <br />
          <PurchaseOrderTable
            cv phaseId={this.state.phase.id} documents={this.props.documents.data}
          />
          <br />
          <h5>How To Add BJ{"'"}s Product To Warehouse Merchandise Table</h5>
          <br />
          <ul className={S.scriptList}>
            <li>
              In the merchandise table below, click the blue {'\'Edit\''} button
            </li>
            <br />
            <li>
              In the blank row that populates, enter information in the
              following fields:
              <ul>
                <li>
                  PO Number - select the non-billable PO that was just created
                </li>
                <li>Item Name</li>
                <li>
                  QTY Received - enter amount/sqft of surface type material
                </li>
                <li>Date Received</li>
                <li>Note (optional)</li>
                <li>Status - select {'\'On Order\''} from the drop-down list</li>
              </ul>
            </li>
            <br />
            <li>Click the green {'\'Save\''} button</li>
            <li>Click the green {'\'Done\''} button</li>
          </ul>
          <br />
          <WarehouseMerchandiseItemTable
            padInfo={this.state.pads}
            lineItems={this.props.lineItemsWarehouseMerchandise}
            editable={can(this.props.role, 'update', 'warehouse')}
            phaseId={this.state.phase.id}
            cv
          />
          <br />
        </Fragment>}
        <MerchandiseItemTable
          phaseId={this.state.phase.id} phaseSource={this.state.phase.source} cv
        />
        <br />
        <h5>Identifying/Inputting Custom Labor</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            The layout of assessment/refigure docs may vary, so finding custom
            labor may vary from document to document
          </li>
          <li>
            Utilize your computer{'\''}s built-in search function (CTRL+f) to search
            for key words such as: {'\'Custom\''}, {'\'Additional\''}, {'\'Additional Custom\''},
            {'\'Custom Labor\''}, etc. (these are not the only things you can search)
          </li>
          <li>
            If the document has a {'\'Service Items\''} Section, if a item in that
            has {'\'Custom\''} in the {'\'Item Number\''} column, it is a custom labor
          </li>
          <li>
            If the document has a table that starts on the first or second page
            and has a column titled {'\'LN\''}, in that table look for any Labor
            described as custom, and there is also a specific row called
            {'\'Additional Custom Work\''} (it{'\''}s usually {'\'LN 57\''})
          </li>
          <li>Input custom labor in the PO table below (where applicable)</li>
        </ul>
        <br />
        <PhaseLineItemTables cv phaseId={this.state.phase.id} />
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Floor Plan Upload</h4>
      </div>
      <div className={S.checklistContainer}>
        <h5>Uploading The Floor Plan</h5>
        <br />
        <ul className={S.scriptList}>
          <li>
            Once you have confirmed you have the correct floor plan, print the
            floor plan as a pdf (Click the printer icon in the window you are
            viewing the assessment in, select {'\'Save as PDF\''} as the Destination,
            select {'\'Custom\''} then input the page(s) the floor plan is on, such as
            {'\'6\''} or {'\'7-8\''}, as the Pages option, then click {'\'Save\''} and
            save as {'\'CUSTOMER_LAST_NAME_FLOOR_PLAN\''})
          </li>
          <li>
            Upload the newly saved floor plan pdf under {'\'Floor Plan\''} in the
            Upload box below (NEVER use the SPOTBOT floor plan already
            populated in documents, as this is not an accurate floor plan and
            must ALWAYS be marked as {'\'do not use\''})
          </li>
          <li>
            Alternatively, you can drag the document from your Desktop (or if
            open, the folder it was saved in, such as {'\'Documents\''} or
            {'\'Downloads\''}) into the upload box, still saving it as {'\'Floor Plan\''}
          </li>
          <li>
            If you accidentally upload the wrong document, click the grey
            {'\'cancel\''} icon to highlight it in red and mark it as {'\'DO NOT USE\''}
          </li>
        </ul>
        <br />
        <PhaseDocuments
          cv
          parentId={this.state.phase.id}
          parentDocs={folderDocuments(this.props.documents.data)}
        />
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Input Phase Info</h4>
      </div>
      <div className={S.checklistContainer}>
        <ul className={S.scriptList}>
          <li>
            Net SQFT is found on the Flooring Detail Diagram page of the
            assessment
          </li>
          <li>
            If a SPIG measure: the best way to find it is the {'\'total\''} line at
            the bottom of the page
          </li>
          <br />
          <li>
            If an Apex measure:
            <ul>
              <li>
                On this page, you will see 4 columns; Material Type,
                Room, Recommended Material needed, and Actual Room Size
              </li>
              <li>Actual Room Size is considered the {'\'net square footage\''}</li>
              <li>
                Round whenever number has a decimal (e.g. if the overall combined
                actual room size is 2417.2 net square feet, we will round it up to
                2418 square feet)
              </li>
            </ul>
          </li>
        </ul>
        <br />
        <Input
          label="Net Square Feet"
          className={largeInput}
          value={this.state.phase.net_square_footage || ''}
          onChange={val => this.handlePhaseBasic('net_square_footage', val)}
          error={error('net_square_footage', this.props.errors)}
        />
        <ul className={S.scriptList}>
          <li>
            The fields below can be found in various spots, depending on the
            layout of the assessment
          </li>
          <li>
            If the document has a table with a column labeled {'\'LN\''}, a lot of
            these items can be found therein (such as subfloor - usually {'\'LN 27\''},
            {'\'LN 28\''}, {'\'LN 29\''}, {'\'LN 30\''} - items and furniture questions)
          </li>
          <li>
            If there is an {'\'Additional Comments\''} section, this can also contain
            this info, such as subfloor
          </li>
          <li>
            If there is a {'\'Site Details\''}/{'\'Site Questions\''} section,
            things such as power ({'\'Electricity Available\''})
            and climate control ({'\'Regulated Temperature\''})
            can be found there
          </li>
          <li>
            Two other good sections to check are {'\'Detail Specifications\''} and
            {'\'Room Details\''}
          </li>
          <li>
            If you are having trouble finding these in the described sections
            above, they are usually easily found searching for keywords (CTRL+f)
          </li>
        </ul>
        <br />
        <Input
          label="Year Built"
          className={smallInput}
          value={this.state.phase.year_built || ''}
          onChange={val => this.handlePhaseBasic('year_built', val)}
          error={error('year_built', this.props.errors)}
        />
        {(this.state.phase.surface_type.name !== 'Carpet' &&
          this.state.phase.store.delivery_method !== 'None') && <Switch
            name="spigDelivery"
            label="Is SPIG delivering and staging product?"
            onChange={val => this.handlePhaseBasic('spig_delivery', val)}
            checked={this.state.phase.spig_delivery}
          />
        }
        <Switch
          name="hasPower"
          label="Has Power"
          onChange={val => this.handlePhaseSpecific('customer_has_power', val)}
          checked={this.state.phase.phase_specific.customer_has_power}
        />
        <Switch
          name="hasClimateControl"
          label="Has Climate Control"
          onChange={val => this.handlePhaseSpecific('customer_has_climate_control', val)}
          checked={this.state.phase.phase_specific.customer_has_climate_control}
        />
        <Switch
          name="needMoveFurniture"
          label="Moving Furniture"
          onChange={val => this.handlePhaseSpecific('need_move_furniture', val)}
          checked={this.state.phase.phase_specific.need_move_furniture}
        />
        <Switch
          name="needMoveFurnitureOversized"
          label="Moving Oversized Furniture"
          onChange={val => this.handlePhaseSpecific('need_move_furniture_oversized', val)}
          checked={this.state.phase.phase_specific.need_move_furniture_oversized}
        />
        <Switch
          name="needSubfloorPrep"
          label="Subfloor Prep"
          onChange={val => this.handlePhaseSpecific('need_subfloor_prep', val)}
          checked={this.state.phase.phase_specific.need_subfloor_prep}
        />
        <Switch
          name="needSubfloorReplacement"
          label="Subfloor Replacement"
          onChange={val => this.handlePhaseSpecific('need_subfloor_replacement', val)}
          checked={this.state.phase.phase_specific.need_subfloor_replacement}
        />
        {this.state.phase.surface_type.name === 'Carpet' && <Fragment>
          <Switch
            name="needHardSurfaceTransistions"
            label="Need Hard Surface Transistions"
            onChange={val => this.handlePhaseSpecific('need_hardsurface_transitions', val)}
            checked={this.state.phase.phase_specific.need_hardsurface_transitions}
          />
          <Switch
            name="needBaseboardMoulding"
            label="Need Baseboard/Moulding"
            onChange={val => this.handlePhaseSpecific('need_install_baseboard', val)}
            checked={this.state.phase.phase_specific.need_install_baseboard}
          />
          <Switch
            name="needBinding"
            label="Needs Binding"
            onChange={val => this.handlePhaseSpecific('is_binding_carpet', val)}
            checked={this.state.phase.phase_specific.is_binding_carpet}
          />
          <Switch
            name="isPatternMatchCarpet"
            label="Pattern Match Carpet"
            onChange={val => this.handlePhaseSpecific('is_pattern_match_carpet', val)}
            checked={this.state.phase.phase_specific.is_pattern_match_carpet}
          />
          <Switch
            className={styles.specificSwitch}
            name="isBerberCarpet"
            label="Berber Carpet"
            onChange={val => this.handlePhaseSpecific('is_berber_carpet', val)}
            checked={this.state.phase.phase_specific.is_berber_carpet}
            disabled={false}
          />
        </Fragment>}
        {this.state.phase.surface_type.name === 'Hardwood' && <Switch
          className={styles.specificSwitch}
          name="isStepsHardwood"
          label="Hardwood Steps"
          onChange={val => this.handlePhaseSpecific('is_steps_hardwood', val)}
          checked={this.state.phase.phase_specific.is_steps_hardwood}
          disabled={false}
        />}
        {this.props.anchoredRemoval && <Fragment>
          <h6>Which Hardwood Removal?</h6>
          {ANCHORED_REMOVAL_SLUGS.map((slug, index) => <Checkbox
            key={slug}
            checked={this.state.phase.phase_specific[slug]}
            label={slug.replace('need_remove', '').replace(/_/g, ' ').toUpperCase().trim()}
            onChange={val => this.handlePhaseSpecific(slug, val)}
            className={styles.cvCheckboxListItem}
            style={index > 0 ? { marginLeft: '15px' } : {}}
          />)}
          <br />
        </Fragment>}
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Input Contractor Note Info</h4>
      </div>
      <div className={S.checklistContainer}>
        <ul className={S.scriptList}>
          <li>
            Most of this is found in the {'\'Site Details\''} or
            {'\'Site Questions\''} section
          </li>
          <li>
            Installer Notes and Customer Responsibilities can be found in
            additional sections such as {'\'Notes\''}, {'\'Comments\''},
            {'\'Additional Comments\''} (also sometimes there is a standalone
            {'\'Customer Responsibilities\''} section)
          </li>
          <li>Always an option - the computer{'\''}s search command (CTRL+f)</li>
          <li>
            If any of these do not apply to the phase or you cannot find the
            information, select or type {'\'N/A\''}
          </li>
        </ul>
        <br />
        <RadioButtons
          label={'New Construction?'}
          onChange={val => this.handleVal('newConstruction', val)}
          selectedValue={this.state.newConstruction}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Paved Driveway?'}
          onChange={val => this.handleVal('pavedDriveway', val)}
          selectedValue={this.state.pavedDriveway}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Difficult Access?'}
          onChange={val => this.handleVal('difficultAccess', val)}
          selectedValue={this.state.difficultAccess}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Restricted Parking Access?'}
          onChange={val => this.handleVal('restrictedParking', val)}
          selectedValue={this.state.restrictedParking}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'If Multi-Family Unit, Is Elevator Available?'}
          onChange={val => this.handleVal('elevatorAvailable', val)}
          selectedValue={this.state.elevatorAvailable}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Floor/Baseboard Heating?'}
          onChange={val => this.handleVal('floorBaseHeating', val)}
          selectedValue={this.state.floorBaseHeating}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Freshly Painted Walls?'}
          onChange={val => this.handleVal('freshPaintWalls', val)}
          selectedValue={this.state.freshPaintWalls}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Pets Present?'}
          onChange={val => this.handleVal('petsPresent', val)}
          selectedValue={this.state.petsPresent}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Place To Cut Materials?'}
          onChange={val => this.handleVal('cuttingArea', val)}
          selectedValue={this.state.cuttingArea}
          radioButtons={this.buttons()}
        />
        <br />
        <StyledSelect
          className={largeInput}
          label="Building Type"
          onChange={val => this.handleVal('buildingType', val)}
          options={this.buildingTypeOptions()}
          value={this.state.buildingType || ''}
          placeholder={'Select Building Type'}
        />
        {this.state.phase.surface_type.name === 'Tile' && <RadioButtons
          label={'Rapid Setting Mortar?'}
          onChange={val => this.handleVal('rapidSetMortar', val)}
          selectedValue={this.state.rapidSetMortar}
          radioButtons={this.buttons()}
        />}
        {(!this.state.phase.net_square_footage || this.state.phase.net_square_footage < 150) &&
        <Fragment>
          <RadioButtons
            label={'Wood Subfloor?'}
            onChange={val => this.handleVal('woodSubfloor', val)}
            selectedValue={this.state.woodSubfloor}
            radioButtons={this.buttons()}
          />
          {this.state.woodSubfloor === 'Yes' && this.props.phase.surface_type.name === 'Tile' && <RadioButtons
            label={'Backer Board And Mortar?'}
            onChange={val => this.handleVal('hardyBackerMortar', val)}
            selectedValue={this.state.hardyBackerMortar}
            radioButtons={this.buttons()}
          />}
        </Fragment>}
        <RadioButtons
          label={'Are We Supplying Transitions For Rolled Goods?'}
          onChange={val => this.handleVal('rolledGoodsTransitions', val)}
          selectedValue={this.state.rolledGoodsTransitions}
          radioButtons={this.buttons()}
        />
        <RadioButtons
          label={'Pickup Required?'}
          onChange={val => this.handleVal('pickup', val)}
          selectedValue={this.state.pickup}
          radioButtons={this.buttons()}
        />
        <SimpleInput
          label="Installer Note"
          text={this.state.installerNote}
          errors={this.props.errors.installerNote}
          onChange={this.handleInstallerNote}
        />
        <RadioButtons
          label={'Gas Appliance(s) Present?'}
          onChange={val => this.handleVal('gasAppliances', val)}
          selectedValue={this.state.gasAppliances}
          radioButtons={this.buttons()}
        />
        <SimpleInput
          label="Customer Responsibilities"
          text={this.state.customerNote}
          errors={this.props.errors.customerNote}
          onChange={this.handleCustomerNote}
        />
      </div>
    </Card>
    <Card className={S.card}>
      <div className={S.headerContainer}>
        <h4 className={S.title}>Confirm And Submit</h4>
      </div>
      <div className={S.checklistContainer}>
        {this.requiredFieldsMissing() ? (<p className={S.checklistError}>
          {this.requiredFieldsMissing()}
        </p>) : (<Fragment>
          {this.nothingToSubmit() ? (<p className={S.checklistError}>
              Submit some new Phase Info to continue
          </p>) : (<RadioButtons
            label={'Have You Gathered All The Information That You Can?'}
            onChange={val => this.handleReadyToSubmit(val)}
            selectedValue={this.state.readyToSubmit ? 'Yes' : 'No'}
            radioButtons={this.buttons(true)}
          />)}
          <br />
          {!this.nothingToSubmit() && <Fragment>
            {this.state.readyToSubmit ? (<Button
              label="Submit Info And Complete Checklist"
              flat inverse ripple
              className={S.saveButton}
              onClick={() => this.completeCvChecklist()}
            />) : (<p className={S.checklistError}>
              You must verify you are ready to submit to continue
            </p>)}
          </Fragment>}
        </Fragment>)}
      </div>
    </Card>
  </div>;

  render = () => {
    if (this.props.phase.source === 'IFI') return this.renderIfi();
    return this.renderDefault();
  }
}

ContractVerificationChecklist.propTypes = {
  navigate: PropTypes.func.isRequired,
  updatePhase: PropTypes.func.isRequired,
  updatePhaseTask: PropTypes.func.isRequired,
  addEventToPhase: PropTypes.func.isRequired,
  fetchPhaseDocuments: PropTypes.func.isRequired,
  phase: PhaseType,
  role: PropTypes.string,
  documents: PropTypes.object,
  errors: PropTypes.object,
  anchoredRemoval: PropTypes.bool,
  lineItemsWarehousePads: PropTypes.arrayOf(
    PropTypes.shape(phaseLineItemShape),
  ),
  lineItemsWarehouseMerchandise: PropTypes.arrayOf(
    PropTypes.shape(warehouseMerchandiseItem),
  ),
  requestPhaseItems: PropTypes.func,
};

const mapStateToProps = state => ({
  anchoredRemoval: state.phase.phaseLineItems.items.filter(li =>
    li.id > 0 && ANCHORED_REMOVAL_PLIS.includes(li.item_number)).length > 0,
  lineItemsWarehousePads: state.phase.phaseLineItems.items.filter((li) => {
    if (state.phase.source === 'IME') {
      return li.kind === 'Pad';
    }
    return li.kind === 'Product';
  }),
  lineItemsWarehouseMerchandise: state.phase.warehouseMerchandiseItems.items,
  phase: state.phase.details,
  documents: state.phase.documents,
  role: getRole(state),
  errors: getPhaseErrors(state) || {},
});

export default connect(mapStateToProps, {
  navigate,
  updatePhase,
  updatePhaseTask,
  addEventToPhase,
  fetchPhaseDocuments,
  requestPhaseItems,
})(ContractVerificationChecklist);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseContractVerificationChecklist/index.jsx